import type { ConnectedServiceType } from "@yagni/shared";

export const GOOGLE_WORKSPACE_ALL_SCOPES =
  "gmail.modify,calendar.readonly,calendar.events,contacts.readonly," +
  "drive.readonly,drive.file,spreadsheets,documents,presentations";

// Google Analytics has NO install URL: analytics.readonly is a sensitive
// scope the OAuth app is not verified for, so requesting it shows Google's
// unverified-app interstitial. The connector is early-access request-only
// (shared/connectors/planned.ts) until the scope passes verification.

export type OAuthInstallServiceType = Extract<
  ConnectedServiceType,
  | "google_workspace"
  | "slack"
  | "linear"
  | "github"
  | "hubspot"
  | "notion"
  | "stripe"
>;

export function googleWorkspaceInstallUrl(
  returnTo: string,
  options: { addAccount?: boolean } = {},
): string {
  const scopes = encodeURIComponent(GOOGLE_WORKSPACE_ALL_SCOPES);
  const addAccountParam = options.addAccount ? "&add_account=1" : "";
  return `/api/auth/google/workspace?scopes=${scopes}&return_to=${encodeReturnTo(returnTo)}${addAccountParam}`;
}

/**
 * HubSpot install URL. Write access is OPT-IN: the default connect requests
 * only read-only CRM scopes, because HubSpot's consent screen has no per-scope
 * toggle (every scope shown is granted together), so the only way to offer a
 * genuinely read-only connect is to not request the write scopes at all. Pass
 * `{ writes: true }` for the "Enable write access" path, which adds the write
 * scopes the reversible draft tools need (YAG-203).
 */
export function hubspotInstallUrl(
  returnTo: string,
  options: { writes?: boolean } = {},
): string {
  const writesParam = options.writes ? "writes=1&" : "";
  return `/api/auth/hubspot/install?${writesParam}return_to=${encodeReturnTo(returnTo)}`;
}

export function oauthInstallUrl(
  serviceType: ConnectedServiceType,
  returnTo: string,
  options: { githubMode?: "authorize"; site?: string } = {},
): string {
  if (serviceType === "google_workspace") {
    return googleWorkspaceInstallUrl(returnTo);
  }

  const mode =
    serviceType === "github" && options.githubMode
      ? `mode=${encodeURIComponent(options.githubMode)}&`
      : "";
  // Jira: the Atlassian site (cloud id) the person picked when their account
  // reaches more than one.
  const site =
    serviceType === "jira" && options.site ? `site=${encodeURIComponent(options.site)}&` : "";
  return `/api/auth/${serviceType}/install?${mode}${site}return_to=${encodeReturnTo(returnTo)}`;
}

export interface JiraSiteChoice {
  id: string;
  url: string;
  name: string;
}

/**
 * The `jiraSites` param the Jira callback sends with `jiraError=choose_site`:
 * base64url JSON of the sites the grant reaches. Empty on anything malformed.
 */
export function decodeJiraSites(raw: string | null): JiraSiteChoice[] {
  if (!raw) return [];
  try {
    const base64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!Array.isArray(parsed)) return [];
    const sites: JiraSiteChoice[] = [];
    for (const entry of parsed as Array<Record<string, unknown> | null>) {
      if (entry && typeof entry.id === "string" && entry.id) {
        sites.push({
          id: entry.id,
          url: typeof entry.url === "string" ? entry.url : "",
          name: typeof entry.name === "string" && entry.name ? entry.name : entry.id,
        });
      }
    }
    return sites;
  } catch {
    return [];
  }
}

/**
 * Personal-connection install URL (per-user write identity, spec 2026-08-09).
 * Same OAuth app as the workspace install; the callback stores an owner-scoped
 * personal row so writes YAGNI makes for this member are attributed to their
 * own account. Jira and Linear today.
 */
export function personalOauthInstallUrl(
  serviceType: ConnectedServiceType,
  returnTo: string,
): string {
  return `/api/auth/${serviceType}/install?personal=1&return_to=${encodeReturnTo(returnTo)}`;
}

function encodeReturnTo(returnTo: string): string {
  return returnTo.includes("?") ? encodeURIComponent(returnTo) : returnTo;
}

/**
 * Connectors whose web OAuth URL gets hijacked by a same-named desktop app when
 * opened via a same-tab navigation: the OS hands the link to the installed app
 * instead of letting the browser follow it, so the user never sees the consent
 * screen. Open these in a new tab (the proven workaround) so the browser keeps
 * the OAuth flow. Notion is the known case — its desktop app registers as a
 * handler for notion.so / api.notion.com links.
 */
const DESKTOP_APP_OAUTH_SERVICES = new Set<ConnectedServiceType>(["notion"]);

export function oauthOpensDesktopApp(serviceType: ConnectedServiceType): boolean {
  return DESKTOP_APP_OAUTH_SERVICES.has(serviceType);
}
